import * as userActions from './actions';

import { catchError, concatMap, filter, map, mapTo, switchMap, withLatestFrom } from 'rxjs/operators';

import { AjaxError } from 'rxjs/ajax';
import { Epic } from 'redux-observable';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import { RootState } from 'store/reducer';
import { NotFound } from 'store/user/errors';
import { isServerUnreachable } from 'store/user/network-failure';
import { isActionOf } from 'typesafe-actions';
import { of } from 'rxjs';
import { transformErrorIntoSerializableError } from 'store/utils';
import qs from 'qs';
import { replace } from 'store/router/actions';
import { getBuilding } from 'store/building/actions';
import { getBuildingTheme } from 'store/theme/actions';
import { getResourceTimeRanges } from 'store/resource-time-ranges/actions';
import { buildResourceTimeRangesRefetchParams } from 'utils/build-resource-time-ranges-refetch-params';

const UNAUTHORIZED_ERROR_CODE = 401;
const NOT_FOUND_ERROR_CODE = 404;
const BAD_REQUEST_ERROR_CODE = 400;
const INTERNAL_SERVER_ERROR_CODE = 500;
// Mapped to NotFound, which the middleware treats as "re-authenticate". Unreachable-server statuses
// (0/408/429/502/503/504, see network-failure.ts) are deliberately absent: they keep their status and are retried.
const errors = [UNAUTHORIZED_ERROR_CODE, NOT_FOUND_ERROR_CODE, BAD_REQUEST_ERROR_CODE, INTERNAL_SERVER_ERROR_CODE];

export const externalLoginRedirect: Epic<RootAction, RootAction, RootState> = action$ =>
  action$.pipe(
    filter(isActionOf(userActions.getCurrentUser.success)),
    map(() => {
      const search: string = window.location?.search ?? '';
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { authToken, apiUrl, ...restSearch } = qs.parse(search.replace(/^\?/, ''));
      const newSearchString = qs.stringify(restSearch);
      const path = window.location?.pathname ?? '/';

      return replace(newSearchString ? `${path}?${newSearchString}` : path);
    }),
  );

/*
 * ⛔ NOT FROM THE BARE ROOT. Replacing `/` with `/` goes nowhere but is still a navigation -- every
 * history listener and every route runs again -- and in the iOS webview a failed user fetch at the
 * root (status 0, the request never left) was part of the `/`->`/` churn WebKit ends with
 * "history.replaceState() more than 100 times per 10 seconds" (Sentry RESOURCE-BOOKING 5260489917):
 * the screen the tenant sees as loading forever with nothing clickable.
 *
 * Only the exact no-op is skipped. A root WITH a query still redirects, because dropping the query
 * is part of the point: a stale `authToken` left in it would be read back in by `useViewParams`.
 */
const isAtBareRoot = (): boolean => window.location?.pathname === '/' && !window.location?.search;

export const externalLoginFailureRedirect: Epic<RootAction, RootAction, RootState> = action$ =>
  action$.pipe(
    filter(isActionOf(userActions.getCurrentUser.failure)),
    // An unreachable server is retried in place; keep the route (e.g. a deeplink's `/receipt/<uuid>`).
    filter(({ payload }) => !isServerUnreachable(payload.error)),
    filter(() => !isAtBareRoot()),
    mapTo(replace('/')),
  );

export const getCurrentUser: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(userActions.getCurrentUser.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) =>
      apiClient(state)
        .getCurrentUser(payload)
        .pipe(
          map(xhrPayload => {
            return userActions.getCurrentUser.success({ user: xhrPayload.response });
          }),
          catchError((error: Error) => {
            if (error instanceof AjaxError && errors.includes(error.status)) {
              return of(
                userActions.getCurrentUser.failure({
                  error: transformErrorIntoSerializableError(new NotFound()),
                }),
              );
            }

            return of(userActions.getCurrentUser.failure({ error: transformErrorIntoSerializableError(error) }));
          }),
        ),
    ),
  );

export const refetchAfterRefreshToken: Epic<RootAction, RootAction, RootState, RootDependencies> = (action$, state$) =>
  action$.pipe(
    filter(isActionOf(userActions.refreshToken)),
    withLatestFrom(state$),
    concatMap(([, state]) => {
      const timeRangesParams = buildResourceTimeRangesRefetchParams(state);

      return of(
        getBuilding.request(state.config.buildingUuid),
        getBuildingTheme.request(state.config.buildingUuid),
        userActions.getCurrentUser.request({
          authToken: state.config.authToken,
          appBrand: state.config.appBrand,
          buildingUuid: state.config.buildingUuid,
        }),
        ...(timeRangesParams ? [getResourceTimeRanges.request(timeRangesParams)] : []),
      );
    }),
  );
